/**
 * Suchfelder gegen JTL - Constraint 16 des Phasenplans, woertlich:
 *
 *   "Suchfelder gegen JTL: Debounce 300 ms, Mindestlaenge 2 Zeichen, serverseitig auf Top-50
 *    Treffer begrenzt, LRU-Cache 60 s je normalisiertem Suchbegriff, Single-Flight-Guard."
 *
 * Dieselben fuenf Groessen stehen in der Backend-Spec ("Suche: serverseitig, gegen JTL, mit
 * Eingabe-Debounce von 300 ms und Mindestlaenge 2 Zeichen ... Kleiner LRU-Cache pro normalisiertem
 * Suchbegriff (60 s) ... Single-Flight-Guard: identische gleichzeitige Suchen teilen sich einen
 * Abruf").
 *
 * Dies ist KEINE Portierung: der Bestand tippt Artikel und Lieferanten frei ein
 * (`index.html:8496`, `patchItem(i,'name',...)`). Die Suche ist Verhaltensaenderung 3 der Spec und
 * damit eine der beiden ausgenommenen Flaechen dieses Tasks
 * (`test/visual/ausnahmen.ts`, `Drawer/MainTab/Positionsbearbeitung`).
 *
 * ================= Die fuenf Groessen, je einzeln nachpruefbar =================
 *
 * 1. MINDESTLAENGE 2 - unter zwei Zeichen (nach Normalisierung) wird gar nicht erst abgefragt; die
 *    Zusage loest sofort mit einer leeren Trefferliste auf und ein laufender Debounce wird
 *    verworfen. Ein Aufruf mit einem Zeichen darf keine Anfrage ausloesen, auch nicht verzoegert.
 * 2. DEBOUNCE 300 ms - jeder Aufruf verschiebt die Abfrage; erst 300 ms Ruhe loesen sie aus. Ein
 *    Aufruf bei 299 ms hat noch nichts abgefragt.
 * 3. TOP-50 - die Grenze reist als `grenze` mit zum Abruf (serverseitig, wie die Spec verlangt).
 *    Zusaetzlich wird die Antwort beschnitten: ein Server, der sich nicht daran haelt, darf die
 *    Oberflaeche nicht mit 5.000 Zeilen fluten.
 * 4. LRU-CACHE 60 s je normalisiertem Begriff - "Rohr", "ROHR " und " rohr" sind derselbe Eintrag.
 *    Ein Treffer im Cache antwortet SOFORT (kein Debounce, kein Abruf). Nach 60 s ist der Eintrag
 *    alt und wird verworfen. Die Kapazitaet ist begrenzt; verdraengt wird der am laengsten nicht
 *    benutzte Eintrag - deshalb LRU und nicht bloss "Cache".
 * 5. SINGLE-FLIGHT - laeuft zu einem Begriff bereits ein Abruf, teilen sich alle weiteren Aufrufer
 *    dessen Zusage, statt einen zweiten Abruf zu starten.
 *
 * ================= Zwei bewusste Festlegungen =================
 *
 * - Ein durch einen neueren Aufruf VERDRAENGTER Aufruf loest mit dem Ergebnis des neueren auf. Eine
 *   Suchbox zeigt ohnehin nur den letzten Stand; eine Zusage, die nie auflöst, waere eine Falle fuer
 *   jeden Aufrufer, der sie `await`et.
 * - Die Uhr kommt ueber `jetzt()` herein (Vorgabe `Date.now`). Ohne diese Naht liesse sich der
 *   60-Sekunden-Ablauf nur mit echter Wartezeit pruefen.
 *
 * ================= HERKUNFT DES PFADS (Merge `feat/09-spa-seiten` -> `feat/09-react-spa`) =========
 *
 * Diese Datei hiess auf `feat/09-spa-seiten` `api/jtlSuche.ts` (Task 17). Unter demselben Pfad lag
 * auf `feat/09-react-spa` eine unabhaengig entstandene, ANDERE Fassung (Task 23/25) - ein
 * add/add-Konflikt, dessen von dort vorgeschriebene Probe ("erst Bytegleichheit des Rumpfs pruefen")
 * durchgefallen ist: die beiden unterscheiden sich in Optionsnamen, Cacheinhalt, Cache-Treffer-
 * Verhalten, Umgang mit zu kurzen Begriffen, Uhr-Einhaengung und im fuenften Merkmal von
 * Constraint 16 (Top-50), das NUR diese Fassung baut. Die vollstaendige Gegenueberstellung steht im
 * Kopfkommentar von `api/jtlSuche.ts`.
 *
 * Der Merge hat die Pfade getrennt, statt eine Seite zu nehmen - beide Fassungen haben eine eigene,
 * gruene Testdatei (8 Faelle hier, 6 dort), und jede Wahl haette die Zusicherungen der anderen Seite
 * fallen gelassen. Am Rumpf und an den Tests dieser Fassung wurde NICHTS geaendert; angepasst wurden
 * ausschliesslich der Dateiname und die vier Importpfade darauf
 * (`Drawer/MainTab/Positionen.tsx`, `Drawer/MainTab/typen.ts`,
 * `test/komponenten/api/jtlArtikelSuche.test.ts`, `test/komponenten/Drawer/positionen.test.tsx`).
 *
 * ⚠ OFFENE ENTSCHEIDUNG: siehe `api/jtlSuche.ts`. Die Zusammenfuehrung auf EINE Suche - voraussichtlich
 * auf diese Fassung - ist ein eigener Task, kein Merge.
 */
import { normSearch } from "../lib/format.ts";

/** Constraint 16, woertlich - die fuenf Groessen an genau einer Stelle. */
export const JTL_SUCHE_DEBOUNCE_MS = 300;
export const JTL_SUCHE_MINDESTLAENGE = 2;
export const JTL_SUCHE_HOECHSTZAHL = 50;
export const JTL_SUCHE_CACHE_DAUER_MS = 60_000;
/** Kapazitaet des LRU-Caches ("kleiner LRU-Cache", Backend-Spec) - nicht in Constraint 16 beziffert. */
export const JTL_SUCHE_CACHE_GROESSE = 32;

export interface JtlAbrufOptionen {
  /** Top-N; reist zum Server, weil die Spec die Begrenzung SERVERSEITIG verlangt. */
  grenze: number;
  signal?: AbortSignal;
  /**
   * Optionaler Suchkontext (z. B. eine Lieferanten-Kennung), NEBEN dem eigentlichen Suchbegriff -
   * geht nicht in den Cache-/Single-Flight-Schluessel als Textbestandteil des Begriffs ein, sondern
   * bildet mit ihm zusammen einen zusammengesetzten Schluessel (siehe `abrufen`/`ausCache`/`inCache`
   * unten), damit derselbe Begriff mit verschiedenem Kontext getrennte Cache-Eintraege bekommt.
   */
  kontext?: string;
}

export interface JtlSucheOptionen<T> {
  /** Der eigentliche Abruf. Bekommt den NORMALISIERTEN Begriff. */
  abruf: (begriff: string, optionen: JtlAbrufOptionen) => Promise<readonly T[]>;
  verzoegerungMs?: number;
  mindestLaenge?: number;
  hoechstzahl?: number;
  cacheDauerMs?: number;
  cacheGroesse?: number;
  /** Die Uhr des Caches. Vorgabe `Date.now`. */
  jetzt?: () => number;
}

interface CacheEintrag<T> {
  treffer: T[];
  /** Zeitpunkt der ANTWORT - der Ablauf zaehlt ab da, nicht ab der Frage. */
  erfasst: number;
}

/**
 * `kontext` ist optional und geht NICHT in den Suchbegriff selbst ein, sondern haengt getrennt
 * daneben - Aufrufer ohne Kontext (der bisherige Bestand) rufen weiterhin mit einem Argument auf.
 */
export type JtlSuche<T> = (begriff: string, kontext?: string) => Promise<T[]>;

export function jtlSuche<T>(optionen: JtlSucheOptionen<T>): JtlSuche<T> {
  const {
    abruf,
    verzoegerungMs = JTL_SUCHE_DEBOUNCE_MS,
    mindestLaenge = JTL_SUCHE_MINDESTLAENGE,
    hoechstzahl = JTL_SUCHE_HOECHSTZAHL,
    cacheDauerMs = JTL_SUCHE_CACHE_DAUER_MS,
    cacheGroesse = JTL_SUCHE_CACHE_GROESSE,
    jetzt = Date.now,
  } = optionen;

  // Map bewahrt die Einfuegereihenfolge; "zuletzt benutzt" wird durch Loeschen und Neusetzen
  // nachgefuehrt - der erste Schluessel ist damit stets der am laengsten nicht benutzte.
  const cache = new Map<string, CacheEintrag<T>>();
  const laufend = new Map<string, Promise<T[]>>();

  let uhr: ReturnType<typeof setTimeout> | null = null;
  let wartende: ((treffer: T[]) => void)[] = [];
  let wartendeFehler: ((fehler: unknown) => void)[] = [];

  /** Zusammengesetzter Cache-/Single-Flight-Schluessel - derselbe Begriff mit verschiedenem
   *  Kontext ist damit ein EIGENER Eintrag; `abruf` selbst sieht diesen Schluessel nie, nur den
   *  reinen Begriff plus `kontext` getrennt (siehe `abrufen`). */
  const schluesselVon = (begriff: string, kontext: string | undefined): string =>
    `${kontext ?? "-"}:${begriff}`;

  const ausCache = (schluessel: string): T[] | null => {
    const eintrag = cache.get(schluessel);
    if (!eintrag) return null;
    if (jetzt() - eintrag.erfasst >= cacheDauerMs) {
      cache.delete(schluessel);
      return null;
    }
    // Benutzung = juengster Eintrag (LRU).
    cache.delete(schluessel);
    cache.set(schluessel, eintrag);
    return eintrag.treffer;
  };

  const inCache = (schluessel: string, treffer: T[]): void => {
    cache.delete(schluessel);
    cache.set(schluessel, { treffer, erfasst: jetzt() });
    while (cache.size > cacheGroesse) {
      const aeltester = cache.keys().next();
      if (aeltester.done) break;
      cache.delete(aeltester.value);
    }
  };

  const abrufen = (begriff: string, kontext: string | undefined): Promise<T[]> => {
    const schluessel = schluesselVon(begriff, kontext);
    // Single-Flight: derselbe zusammengesetzte Schluessel, derselbe laufende Abruf.
    const schon = laufend.get(schluessel);
    if (schon) return schon;
    const zusage = Promise.resolve(abruf(begriff, { grenze: hoechstzahl, kontext }))
      .then((treffer) => {
        const beschnitten = (treffer || []).slice(0, hoechstzahl);
        inCache(schluessel, beschnitten);
        return beschnitten;
      })
      .finally(() => {
        laufend.delete(schluessel);
      });
    laufend.set(schluessel, zusage);
    return zusage;
  };

  const debounceVerwerfen = (): void => {
    if (uhr !== null) clearTimeout(uhr);
    uhr = null;
  };

  return function suchen(begriff: string, kontext?: string): Promise<T[]> {
    const normalisiert = normSearch(begriff);

    // (1) Mindestlaenge - kein Abruf, auch nicht verzoegert.
    if (normalisiert.length < mindestLaenge) {
      debounceVerwerfen();
      const offen = wartende;
      wartende = [];
      wartendeFehler = [];
      for (const aufloesen of offen) aufloesen([]);
      return Promise.resolve([]);
    }

    // (4) Cache-Treffer - sofort, ohne Debounce und ohne Abruf.
    const gecacht = ausCache(schluesselVon(normalisiert, kontext));
    if (gecacht) return Promise.resolve(gecacht);

    return new Promise<T[]>((aufloesen, ablehnen) => {
      wartende.push(aufloesen);
      wartendeFehler.push(ablehnen);
      // (2) Debounce - jeder Aufruf verschiebt die Abfrage.
      if (uhr !== null) clearTimeout(uhr);
      uhr = setTimeout(() => {
        uhr = null;
        const offen = wartende;
        const offeneFehler = wartendeFehler;
        wartende = [];
        wartendeFehler = [];
        abrufen(normalisiert, kontext).then(
          (treffer) => {
            for (const fertig of offen) fertig(treffer);
          },
          (fehler) => {
            for (const scheitern of offeneFehler) scheitern(fehler);
          },
        );
      }, verzoegerungMs);
    });
  };
}

/** Ein Artikeltreffer aus JTL, so wie die Positionsbearbeitung ihn braucht. */
export interface JtlArtikel {
  sku?: unknown;
  name?: unknown;
  ean?: unknown;
  /**
   * Ob der Artikel in JTL ein Bild hat (`GET /api/jtl/articles`, seit Auftrag "hatBild vorab
   * mitgeben", 2026-08-27) - roh durchgereicht, kein Mapper-Schritt dazwischen (siehe `App.tsx`,
   * `jtlArtikelSuche`: `anfrage<JtlArtikel[]>` liest die Backend-Antwort unveraendert).
   */
  hatBild?: boolean;
  [feld: string]: unknown;
}
